import Termly from 'termly-namespace'

import CurrentRegionSettings from 'constants/current-region-settings'

import CookieConsents, {
  CONSENT_LIFETIME_SECONDS,
} from 'utils/cookieConsents'
import GTM from 'utils/gtm'
import shouldAutoGrant from 'utils/shouldAutoGrant'


const GCM_OPT_IN_DEFAULTS = {
  ad_personalization: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted',
  social_storage: 'denied',
  // Not really a thing, but this is what I do in the GTM template, so
  // I'm going to use it here to be consistent.
  unclassified_storage: 'denied',
}

const GCM_OPT_OUT_DEFAULTS = {
  ad_personalization: 'granted',
  ad_storage: 'granted',
  ad_user_data: 'granted',
  analytics_storage: 'granted',
  functionality_storage: 'granted',
  personalization_storage: 'granted',
  security_storage: 'granted',
  social_storage: 'granted',
  // Not really a thing, but this is what I do in the GTM template, so
  // I'm going to use it here to be consistent.
  unclassified_storage: 'granted',
}


export default function sendGCMConsentDefaults() {
  if ( window.TERMLY_FORCE_DISABLE_GCM ) {
    console.debug('[Termly] Not sending GCM consent defaults '
                  + '(found window.TERMLY_FORCE_DISABLE_GCM = %o)',
                  window.TERMLY_FORCE_DISABLE_GCM)
    return
  }

  if ( !CurrentRegionSettings.isInitialized() ) {
    console.debug('[Termly] Cannot set GCM consent defaults: region settings have not yet been initialized.')
    return
  }

  const defaults = getDefaults()

  // Identify Termly as the CMP behind the consent signal. This has to go out
  // before the signal itself, and on every path -- including regions with the
  // banner disabled, where none of the rest of our startup sequence runs.
  GTM.initCMP()

  GTM.sendConsentDefault(defaults)

  Termly.captureLateConsentSnapshot({
    hasWaitForUpdate: 'wait_for_update' in defaults,
  })

  if ( CurrentRegionSettings.isWidgetEnabled() ) {
    sendSavedConsents()
  }
}

// Google wants a returning visitor's choice sent as an update after the
// default. onAllApiRespond() sends it too, but only once the page is ready,
// and a tag that fires before then reads the default. Sending it here, in the
// same pass as the default, leaves no gap for a tag to fire in.
//
function sendSavedConsents() {
  const savedConsents = CookieConsents.getConsents(CONSENT_LIFETIME_SECONDS)

  if ( !savedConsents ) {
    return
  }

  console.debug('[Termly] Sending the consents this visitor already saved as a GCM update')

  GTM.sendConsentUpdate(savedConsents)
}

function getDefaults() {
  const gcmSettings = CurrentRegionSettings.getGoogleConsentModeSettings()

  // When the CMP is disabled for this region, Google requires us to grant
  // consent so tags still fire ("grant ... where the banner does not appear"),
  // regardless of the region's opt-in/opt-out mode. A GPC signal is the only
  // thing that holds that back. We force the category states here but keep any
  // dashboard Data Transmission Controls (ads_data_redaction, url_passthrough),
  // which apply regardless of region.
  //
  // A region that merely suppresses its banner does not come through here. It
  // still has `enable_widget`, so it takes the normal path below and keeps its
  // Advanced Consent Mode defaults, `wait_for_update` included. Sending it down
  // this path would strip that from the customers most likely to want it.
  if ( !CurrentRegionSettings.isWidgetEnabled() ) {
    const hasGPC = Boolean(navigator.globalPrivacyControl)
    const categories = ( hasGPC ) ? GCM_OPT_IN_DEFAULTS : GCM_OPT_OUT_DEFAULTS

    if ( hasGPC ) {
      console.debug('[Termly] CMP disabled for this region with GPC signal; forcing GCM opt-in defaults')
    } else {
      console.debug('[Termly] CMP disabled for this region; forcing GCM opt-out defaults')
    }

    if ( gcmSettings ) {
      const regionSettings = getGCMRegionSettings(gcmSettings)

      // We deliberately drop wait_for_update on this path because
      // leaving it in when the CMP is disabled would tell tags to
      // wait for an update that never arrives, leaving them
      // stranded. Without it, the granted default releases them
      // immediately.
      delete regionSettings.wait_for_update

      return {
        ...regionSettings,
        ...categories,
      }
    }

    return categories
  }

  if ( gcmSettings ) {
    console.debug('[Termly] Using GCM defaults from dashboard region configuration')
    return getGCMRegionSettings(gcmSettings)
  }

  if ( shouldAutoGrant() ) {
    console.debug('[Termly] Using standard GCM opt-out defaults')
    return GCM_OPT_OUT_DEFAULTS
  }

  if ( CurrentRegionSettings.isOptOut() ) {
    console.debug('[Termly] GPC signal detected; suppressing opt-out auto-grant of GCM defaults')
  } else {
    console.debug('[Termly] Using standard GCM opt-in defaults')
  }

  return GCM_OPT_IN_DEFAULTS
}

function getGCMRegionSettings(settings) {
  const {
    enable_wait_for_update: enableWaitForUpdate,
    ...gcmSettings
  } = settings

  if ( !enableWaitForUpdate ) {
    delete gcmSettings.wait_for_update
  }

  return gcmSettings
}
